// src/api/constants.ts

// ============================================
// API CONFIGURATION
// ============================================
// This is a Create React App build, so only REACT_APP_* variables reach the
// bundle — a VITE_* prefix is silently ignored. Set these in .env.local to
// point at a local backend:
//   REACT_APP_API_BASE_URL=http://127.0.0.1:8000/api/
const DEFAULT_API_BASE_URL = 'https://parish-fellowship-backend.vercel.app/api/';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL;

/**
 * Media is served by the same host as the API, so it is derived from the API
 * URL rather than hard-coded — pointing this at localhost broke every uploaded
 * image on the deployed site.
 */
export const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  API_BASE_URL.replace(/\/api\/?$/, '/media/');

// ============================================
// AUTH STORAGE KEYS
// ============================================
// Named here so the axios client, the auth context and the route guards can
// never drift onto different keys again.
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const USER_KEY = 'user';

// ============================================
// PAGINATION
// ============================================
export const INITIAL_PAGE_SIZE = 20;

// ============================================
// USER TYPES
// ============================================
export const USER_TYPE_SYSTEM_ADMIN = 'system_admin';
export const USER_TYPE_EDIFY_STAFF = 'edify_staff';
export const USER_TYPE_PARISH_COORDINATOR = 'parish_coordinator';
export const USER_TYPE_SCHOOL_PROPRIETOR = 'school_proprietor';
export const USER_TYPE_SCHOOL_DIRECTOR = 'school_director';
export const USER_TYPE_TEACHER = 'teacher';
export const USER_TYPE_ACCOUNTANT = 'accountant';
export const USER_TYPE_IT_TEACHER = 'it_teacher';
export const USER_TYPE_CT_TEACHER = 'ct_teacher';

export const USER_TYPE_OPTIONS = [
  { value: USER_TYPE_SYSTEM_ADMIN, label: "System Admin" },
  { value: USER_TYPE_EDIFY_STAFF, label: "Edify Staff" },
  { value: USER_TYPE_PARISH_COORDINATOR, label: "Parish Coordinator" },
  { value: USER_TYPE_SCHOOL_PROPRIETOR, label: "School Proprietor" },
  { value: USER_TYPE_SCHOOL_DIRECTOR, label: "School Director" },
  { value: USER_TYPE_TEACHER, label: "Teacher" },
  { value: USER_TYPE_ACCOUNTANT, label: "Accountant" },
  { value: USER_TYPE_IT_TEACHER, label: "IT Teacher" },
  { value: USER_TYPE_CT_TEACHER, label: "Christian Teaching Teacher" },
];

// ============================================
// SCHOOL TYPES
// ============================================
export const SCHOOL_TYPE_PRIMARY = 'primary';
export const SCHOOL_TYPE_SECONDARY = 'secondary';
export const SCHOOL_TYPE_NURSERY = 'nursery';
export const SCHOOL_TYPE_MIXED = 'mixed';

export const SCHOOL_TYPE_OPTIONS = [
  { value: SCHOOL_TYPE_NURSERY, label: "Nursery" },
  { value: SCHOOL_TYPE_PRIMARY, label: "Primary" },
  { value: SCHOOL_TYPE_SECONDARY, label: "Secondary" },
  { value: SCHOOL_TYPE_MIXED, label: "Mixed" },
];

// ============================================
// OWNERSHIP TYPES
// ============================================
export const OWNERSHIP_PRIVATE = 'private';
export const OWNERSHIP_CHURCH = 'church_owned';
export const OWNERSHIP_NGO = 'ngo';

export const OWNERSHIP_TYPE_OPTIONS = [
  { value: OWNERSHIP_PRIVATE, label: "Private" },
  { value: OWNERSHIP_CHURCH, label: "Church Owned" },
  { value: OWNERSHIP_NGO, label: "NGO" },
];

// ============================================
// LEADERSHIP POSITIONS
// ============================================
export const POSITION_PROPRIETOR = 'proprietor';
export const POSITION_DIRECTOR = 'director';
export const POSITION_DEPUTY_DIRECTOR = 'deputy_director';
export const POSITION_HEAD_TEACHER = 'head_teacher';
export const POSITION_BURSAR = 'bursar';

export const LEADERSHIP_POSITION_OPTIONS = [
  { value: POSITION_PROPRIETOR, label: "Proprietor" },
  { value: POSITION_DIRECTOR, label: "Director" },
  { value: POSITION_DEPUTY_DIRECTOR, label: "Deputy Director" },
  { value: POSITION_HEAD_TEACHER, label: "Head Teacher" },
  { value: POSITION_BURSAR, label: "Bursar" },
];

// ============================================
// DEPARTMENTS
// ============================================
export const DEPT_ACADEMICS = 'academics';
export const DEPT_FINANCE = 'finance';
export const DEPT_IT = 'it';
export const DEPT_CHRISTIAN_TEACHING = 'christian_teaching';
export const DEPT_ADMINISTRATION = 'administration';
export const DEPT_SPORTS = 'sports';

export const DEPARTMENT_OPTIONS = [
  { value: DEPT_ACADEMICS, label: "Academics" },
  { value: DEPT_FINANCE, label: "Finance" },
  { value: DEPT_IT, label: "IT" },
  { value: DEPT_CHRISTIAN_TEACHING, label: "Christian Teaching" },
  { value: DEPT_ADMINISTRATION, label: "Administration" },
  { value: DEPT_SPORTS, label: "Sports" },
];

// ============================================
// CONTRACT TYPES
// ============================================
export const CONTRACT_PERMANENT = 'permanent';
export const CONTRACT_CONTRACT = 'contract';
export const CONTRACT_PART_TIME = 'part_time';

export const CONTRACT_TYPE_OPTIONS = [
  { value: CONTRACT_PERMANENT, label: "Permanent" },
  { value: CONTRACT_CONTRACT, label: "Contract" },
  { value: CONTRACT_PART_TIME, label: "Part Time" },
];

// ============================================
// MEETING TYPES
// ============================================
export const MEETING_PARISH_FELLOWSHIP = 'parish_fellowship';
export const MEETING_PLC = 'plc';
export const MEETING_TRAINING = 'training';
export const MEETING_PLANNING = 'planning';

export const MEETING_TYPE_OPTIONS = [
  { value: MEETING_PARISH_FELLOWSHIP, label: "Parish Fellowship" },
  { value: MEETING_PLC, label: "PLC" },
  { value: MEETING_TRAINING, label: "Training" },
  { value: MEETING_PLANNING, label: "Planning" },
];

// ============================================
// MEETING STATUS
// ============================================
export const STATUS_SCHEDULED = 'scheduled';
export const STATUS_IN_PROGRESS = 'in_progress';
export const STATUS_COMPLETED = 'completed';
export const STATUS_CANCELLED = 'cancelled';

export const MEETING_STATUS_OPTIONS = [
  { value: STATUS_SCHEDULED, label: "Scheduled" },
  { value: STATUS_IN_PROGRESS, label: "In Progress" },
  { value: STATUS_COMPLETED, label: "Completed" },
  { value: STATUS_CANCELLED, label: "Cancelled" },
];

// ============================================
// ATTENDEE ROLES
// ============================================
export const ATTENDEE_PROPRIETOR = 'proprietor';
export const ATTENDEE_DIRECTOR = 'director';
export const ATTENDEE_TEACHER = 'teacher';
export const ATTENDEE_COORDINATOR = 'coordinator';
export const ATTENDEE_OTHER = 'other';

export const ATTENDEE_ROLE_OPTIONS = [
  { value: ATTENDEE_PROPRIETOR, label: "Proprietor" },
  { value: ATTENDEE_DIRECTOR, label: "Director" },
  { value: ATTENDEE_TEACHER, label: "Teacher" },
  { value: ATTENDEE_COORDINATOR, label: "Coordinator" },
  { value: ATTENDEE_OTHER, label: "Other" },
];

// ============================================
// PRIORITY LEVELS
// ============================================
export const PRIORITY_HIGH = 'high';
export const PRIORITY_MEDIUM = 'medium';
export const PRIORITY_LOW = 'low';

export const PRIORITY_LEVEL_OPTIONS = [
  { value: PRIORITY_HIGH, label: "High" },
  { value: PRIORITY_MEDIUM, label: "Medium" },
  { value: PRIORITY_LOW, label: "Low" },
];

// ============================================
// ACTION STATUS
// ============================================
export const ACTION_PENDING = 'pending';
export const ACTION_IN_PROGRESS = 'in_progress';
export const ACTION_COMPLETED = 'completed';
export const ACTION_OVERDUE = 'overdue';

export const ACTION_STATUS_OPTIONS = [
  { value: ACTION_PENDING, label: "Pending" },
  { value: ACTION_IN_PROGRESS, label: "In Progress" },
  { value: ACTION_COMPLETED, label: "Completed" },
  { value: ACTION_OVERDUE, label: "Overdue" },
];

// ============================================
// RESOURCE TYPES
// ============================================
export const RESOURCE_DOCUMENT = 'document';
export const RESOURCE_PRESENTATION = 'presentation';
export const RESOURCE_VIDEO = 'video';
export const RESOURCE_HANDBOOK = 'handbook';
export const RESOURCE_SCORECARD = 'scorecard';

export const RESOURCE_TYPE_OPTIONS = [
  { value: RESOURCE_DOCUMENT, label: "Document" },
  { value: RESOURCE_PRESENTATION, label: "Presentation" },
  { value: RESOURCE_VIDEO, label: "Video" },
  { value: RESOURCE_HANDBOOK, label: "Handbook" },
  { value: RESOURCE_SCORECARD, label: "Scorecard" },
];

// ============================================
// ASSESSMENT/SSA STATUS
// ============================================
export const SSA_DRAFT = 'draft';
export const SSA_SUBMITTED = 'submitted';
export const SSA_UNDER_REVIEW = 'under_review';
export const SSA_VALIDATED = 'validated';
export const SSA_REJECTED = 'rejected';

export const ASSESSMENT_STATUS_OPTIONS = [
  { value: SSA_DRAFT, label: "Draft" },
  { value: SSA_SUBMITTED, label: "Submitted" },
  { value: SSA_UNDER_REVIEW, label: "Under Review" },
  { value: SSA_VALIDATED, label: "Validated" },
  { value: SSA_REJECTED, label: "Rejected" },
];

// ============================================
// OUTCOME AREAS
// ============================================
export const OUTCOME_SUSTAINABILITY = 'sustainability';
export const OUTCOME_DISCIPLESHIP = 'discipleship';
export const OUTCOME_LEARNING = 'learning_outcomes';

export const OUTCOME_AREA_OPTIONS = [
  { value: OUTCOME_SUSTAINABILITY, label: "Sustainability" },
  { value: OUTCOME_DISCIPLESHIP, label: "Discipleship" },
  { value: OUTCOME_LEARNING, label: "Learning Outcomes" },
];

// ============================================
// INDICATOR LEVELS
// ============================================
export const LEVEL_BEGINNING = 1;
export const LEVEL_DEVELOPING = 2;
export const LEVEL_PROFICIENT = 3;
export const LEVEL_ACCOMPLISHED = 4;
export const LEVEL_EXEMPLARY = 5;

export const INDICATOR_LEVEL_OPTIONS = [
  { value: LEVEL_BEGINNING, label: "Beginning" },
  { value: LEVEL_DEVELOPING, label: "Developing" },
  { value: LEVEL_PROFICIENT, label: "Proficient" },
  { value: LEVEL_ACCOMPLISHED, label: "Accomplished" },
  { value: LEVEL_EXEMPLARY, label: "Exemplary" },
];

// ============================================
// TRAINING TYPES
// ============================================
export const TRAINING_HANDBOOK = 'handbook_topic';
export const TRAINING_CUSTOM = 'custom';
export const TRAINING_EXTERNAL = 'external_partner';

export const TRAINING_TYPE_OPTIONS = [
  { value: TRAINING_HANDBOOK, label: "Handbook Topic" },
  { value: TRAINING_CUSTOM, label: "Custom" },
  { value: TRAINING_EXTERNAL, label: "External Partner" },
];

// ============================================
// TARGET AUDIENCE
// ============================================
export const AUDIENCE_ALL = 'all_staff';
export const AUDIENCE_PROPRIETORS = 'proprietors';
export const AUDIENCE_TEACHERS = 'teachers';
export const AUDIENCE_ACCOUNTANTS = 'accountants';
export const AUDIENCE_IT_TEACHERS = 'it_teachers';
export const AUDIENCE_CT_TEACHERS = 'ct_teachers';

export const TARGET_AUDIENCE_OPTIONS = [
  { value: AUDIENCE_ALL, label: "All Staff" },
  { value: AUDIENCE_PROPRIETORS, label: "Proprietors" },
  { value: AUDIENCE_TEACHERS, label: "Teachers" },
  { value: AUDIENCE_ACCOUNTANTS, label: "Accountants" },
  { value: AUDIENCE_IT_TEACHERS, label: "IT Teachers" },
  { value: AUDIENCE_CT_TEACHERS, label: "CT Teachers" },
];

// ============================================
// TRAINER TYPES
// ============================================
export const TRAINER_COORDINATOR = 'coordinator';
export const TRAINER_CHAMPION = 'champion';
export const TRAINER_SCHOOL_LEADER = 'school_leader';
export const TRAINER_EDIFY_STAFF = 'edify_staff';
export const TRAINER_EXTERNAL = 'external_partner';

export const TRAINER_TYPE_OPTIONS = [
  { value: TRAINER_COORDINATOR, label: "Coordinator" },
  { value: TRAINER_CHAMPION, label: "Champion" },
  { value: TRAINER_SCHOOL_LEADER, label: "School Leader" },
  { value: TRAINER_EDIFY_STAFF, label: "Edify Staff" },
  { value: TRAINER_EXTERNAL, label: "External Partner" },
];

// ============================================
// VENUE TYPES
// ============================================
export const VENUE_IN_PERSON = 'in_person';
export const VENUE_VIRTUAL = 'virtual';
export const VENUE_HYBRID = 'hybrid';

export const VENUE_TYPE_OPTIONS = [
  { value: VENUE_IN_PERSON, label: "In Person" },
  { value: VENUE_VIRTUAL, label: "Virtual" },
  { value: VENUE_HYBRID, label: "Hybrid" },
];

// ============================================
// ATTENDANCE STATUS
// ============================================
export const ATTENDANCE_REGISTERED = 'registered';
export const ATTENDANCE_ATTENDED = 'attended';
export const ATTENDANCE_ABSENT = 'absent';
export const ATTENDANCE_CANCELLED = 'cancelled';

export const ATTENDANCE_STATUS_OPTIONS = [
  { value: ATTENDANCE_REGISTERED, label: "Registered" },
  { value: ATTENDANCE_ATTENDED, label: "Attended" },
  { value: ATTENDANCE_ABSENT, label: "Absent" },
  { value: ATTENDANCE_CANCELLED, label: "Cancelled" },
];

// ============================================
// PAYMENT METHODS
// ============================================
export const PAYMENT_CASH = 'cash';
export const PAYMENT_MOBILE_MONEY = 'mobile_money';
export const PAYMENT_BANK_TRANSFER = 'bank_transfer';
export const PAYMENT_CHECK = 'check';

export const PAYMENT_METHOD_OPTIONS = [
  { value: PAYMENT_CASH, label: "Cash" },
  { value: PAYMENT_MOBILE_MONEY, label: "Mobile Money" },
  { value: PAYMENT_BANK_TRANSFER, label: "Bank Transfer" },
  { value: PAYMENT_CHECK, label: "Check" },
];

// ============================================
// PAYMENT STATUS
// ============================================
export const PAYMENT_PENDING = 'pending';
export const PAYMENT_PARTIAL = 'partial';
export const PAYMENT_PAID = 'paid';
export const PAYMENT_OVERDUE = 'overdue';

export const PAYMENT_STATUS_OPTIONS = [
  { value: PAYMENT_PENDING, label: "Pending" },
  { value: PAYMENT_PARTIAL, label: "Partial" },
  { value: PAYMENT_PAID, label: "Paid" },
  { value: PAYMENT_OVERDUE, label: "Overdue" },
];

// ============================================
// REPORT TYPES
// ============================================
export const REPORT_SCHOOL_PERFORMANCE = 'school_performance';
export const REPORT_PARISH_SUMMARY = 'parish_summary';
export const REPORT_ASSESSMENT_TRENDS = 'assessment_trends';
export const REPORT_MEETING_ANALYTICS = 'meeting_analytics';
export const REPORT_TRAINING_IMPACT = 'training_impact';
export const REPORT_FINANCIAL_SUMMARY = 'financial_summary';
export const REPORT_COORDINATOR_PERFORMANCE = 'coordinator_performance';

export const REPORT_TYPE_OPTIONS = [
  { value: REPORT_SCHOOL_PERFORMANCE, label: "School Performance" },
  { value: REPORT_PARISH_SUMMARY, label: "Parish Summary" },
  { value: REPORT_ASSESSMENT_TRENDS, label: "Assessment Trends" },
  { value: REPORT_MEETING_ANALYTICS, label: "Meeting Analytics" },
  { value: REPORT_TRAINING_IMPACT, label: "Training Impact" },
  { value: REPORT_FINANCIAL_SUMMARY, label: "Financial Summary" },
  { value: REPORT_COORDINATOR_PERFORMANCE, label: "Coordinator Performance" },
];

// ============================================
// QUARTERS
// ============================================
export const QUARTER_OPTIONS = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

// ============================================
// UGANDA REGIONS
// ============================================
export const UGANDA_REGIONS = [
  { value: "central", label: "Central" },
  { value: "eastern", label: "Eastern" },
  { value: "northern", label: "Northern" },
  { value: "western", label: "Western" },
];

// ============================================
// CURRENCY
// ============================================
export const DEFAULT_CURRENCY = 'UGX';

// ============================================
// FILE EXTENSIONS
// ============================================
export const ALLOWED_FILE_EXTENSIONS = [
  'pdf', 'png', 'svg', 'jpg', 'jpeg', 
  'docx', 'xlsx', 'doc', 'ppt', 'pptx'
];



























// // src/api/constants.ts

// export const INITIAL_PAGE_SIZE = 20;

// // ============================================
// // USER TYPES
// // ============================================
// export const USER_TYPE_SYSTEM_ADMIN = 'system_admin';
// export const USER_TYPE_EDIFY_STAFF = 'edify_staff';
// export const USER_TYPE_PARISH_COORDINATOR = 'parish_coordinator';
// export const USER_TYPE_SCHOOL_PROPRIETOR = 'school_proprietor';
// export const USER_TYPE_SCHOOL_DIRECTOR = 'school_director';
// export const USER_TYPE_TEACHER = 'teacher';
// export const USER_TYPE_ACCOUNTANT = 'accountant';
// export const USER_TYPE_IT_TEACHER = 'it_teacher';
// export const USER_TYPE_CT_TEACHER = 'ct_teacher';

// export const USER_TYPE_OPTIONS = [
//   { value: USER_TYPE_SYSTEM_ADMIN, label: "System Admin" },
//   { value: USER_TYPE_EDIFY_STAFF, label: "Edify Staff" },
//   { value: USER_TYPE_PARISH_COORDINATOR, label: "Parish Coordinator" },
//   { value: USER_TYPE_SCHOOL_PROPRIETOR, label: "School Proprietor" },
//   { value: USER_TYPE_SCHOOL_DIRECTOR, label: "School Director" },
//   { value: USER_TYPE_TEACHER, label: "Teacher" },
//   { value: USER_TYPE_ACCOUNTANT, label: "Accountant" },
//   { value: USER_TYPE_IT_TEACHER, label: "IT Teacher" },
//   { value: USER_TYPE_CT_TEACHER, label: "Christian Teaching Teacher" },
// ];

// // ============================================
// // SCHOOL TYPES
// // ============================================
// export const SCHOOL_TYPE_PRIMARY = 'primary';
// export const SCHOOL_TYPE_SECONDARY = 'secondary';
// export const SCHOOL_TYPE_NURSERY = 'nursery';
// export const SCHOOL_TYPE_MIXED = 'mixed';

// export const SCHOOL_TYPE_OPTIONS = [
//   { value: SCHOOL_TYPE_NURSERY, label: "Nursery" },
//   { value: SCHOOL_TYPE_PRIMARY, label: "Primary" },
//   { value: SCHOOL_TYPE_SECONDARY, label: "Secondary" },
//   { value: SCHOOL_TYPE_MIXED, label: "Mixed" }, // Changed from "primary_secondary"
// ];

// // ============================================
// // OWNERSHIP TYPES
// // ============================================
// export const OWNERSHIP_PRIVATE = 'private';
// export const OWNERSHIP_CHURCH = 'church_owned';
// export const OWNERSHIP_NGO = 'ngo';

// export const OWNERSHIP_TYPE_OPTIONS = [
//   { value: OWNERSHIP_PRIVATE, label: "Private" },
//   { value: OWNERSHIP_CHURCH, label: "Church Owned" },
//   { value: OWNERSHIP_NGO, label: "NGO" },
//   // Removed "government" - not in backend
// ];

// // ============================================
// // LEADERSHIP POSITIONS
// // ============================================
// export const POSITION_PROPRIETOR = 'proprietor';
// export const POSITION_DIRECTOR = 'director';
// export const POSITION_DEPUTY_DIRECTOR = 'deputy_director';
// export const POSITION_HEAD_TEACHER = 'head_teacher';
// export const POSITION_BURSAR = 'bursar';

// export const LEADERSHIP_POSITION_OPTIONS = [
//   { value: POSITION_PROPRIETOR, label: "Proprietor" },
//   { value: POSITION_DIRECTOR, label: "Director" },
//   { value: POSITION_DEPUTY_DIRECTOR, label: "Deputy Director" },
//   { value: POSITION_HEAD_TEACHER, label: "Head Teacher" },
//   { value: POSITION_BURSAR, label: "Bursar" },
// ];

// // ============================================
// // DEPARTMENTS
// // ============================================
// export const DEPT_ACADEMICS = 'academics';
// export const DEPT_FINANCE = 'finance';
// export const DEPT_IT = 'it';
// export const DEPT_CHRISTIAN_TEACHING = 'christian_teaching';
// export const DEPT_ADMINISTRATION = 'administration';
// export const DEPT_SPORTS = 'sports';

// export const DEPARTMENT_OPTIONS = [
//   { value: DEPT_ACADEMICS, label: "Academics" },
//   { value: DEPT_FINANCE, label: "Finance" },
//   { value: DEPT_IT, label: "IT" },
//   { value: DEPT_CHRISTIAN_TEACHING, label: "Christian Teaching" },
//   { value: DEPT_ADMINISTRATION, label: "Administration" },
//   { value: DEPT_SPORTS, label: "Sports" },
// ];

// // ============================================
// // CONTRACT TYPES
// // ============================================
// export const CONTRACT_PERMANENT = 'permanent';
// export const CONTRACT_CONTRACT = 'contract';
// export const CONTRACT_PART_TIME = 'part_time';

// export const CONTRACT_TYPE_OPTIONS = [
//   { value: CONTRACT_PERMANENT, label: "Permanent" },
//   { value: CONTRACT_CONTRACT, label: "Contract" },
//   { value: CONTRACT_PART_TIME, label: "Part Time" },
// ];

// // ============================================
// // MEETING TYPES
// // ============================================
// export const MEETING_PARISH_FELLOWSHIP = 'parish_fellowship';
// export const MEETING_PLC = 'plc';
// export const MEETING_TRAINING = 'training';
// export const MEETING_PLANNING = 'planning';

// export const MEETING_TYPE_OPTIONS = [
//   { value: MEETING_PARISH_FELLOWSHIP, label: "Parish Fellowship" },
//   { value: MEETING_PLC, label: "PLC" },
//   { value: MEETING_TRAINING, label: "Training" },
//   { value: MEETING_PLANNING, label: "Planning" },
// ];

// // ============================================
// // MEETING STATUS
// // ============================================
// export const STATUS_SCHEDULED = 'scheduled';
// export const STATUS_IN_PROGRESS = 'in_progress';
// export const STATUS_COMPLETED = 'completed';
// export const STATUS_CANCELLED = 'cancelled';

// export const MEETING_STATUS_OPTIONS = [
//   { value: STATUS_SCHEDULED, label: "Scheduled" },
//   { value: STATUS_IN_PROGRESS, label: "In Progress" },
//   { value: STATUS_COMPLETED, label: "Completed" },
//   { value: STATUS_CANCELLED, label: "Cancelled" },
// ];

// // ============================================
// // ATTENDEE ROLES
// // ============================================
// export const ATTENDEE_PROPRIETOR = 'proprietor';
// export const ATTENDEE_DIRECTOR = 'director';
// export const ATTENDEE_TEACHER = 'teacher';
// export const ATTENDEE_COORDINATOR = 'coordinator';
// export const ATTENDEE_OTHER = 'other';

// export const ATTENDEE_ROLE_OPTIONS = [
//   { value: ATTENDEE_PROPRIETOR, label: "Proprietor" },
//   { value: ATTENDEE_DIRECTOR, label: "Director" },
//   { value: ATTENDEE_TEACHER, label: "Teacher" },
//   { value: ATTENDEE_COORDINATOR, label: "Coordinator" },
//   { value: ATTENDEE_OTHER, label: "Other" },
// ];

// // ============================================
// // PRIORITY LEVELS
// // ============================================
// export const PRIORITY_HIGH = 'high';
// export const PRIORITY_MEDIUM = 'medium';
// export const PRIORITY_LOW = 'low';

// export const PRIORITY_LEVEL_OPTIONS = [
//   { value: PRIORITY_HIGH, label: "High" },
//   { value: PRIORITY_MEDIUM, label: "Medium" },
//   { value: PRIORITY_LOW, label: "Low" },
// ];

// // ============================================
// // ACTION STATUS
// // ============================================
// export const ACTION_PENDING = 'pending';
// export const ACTION_IN_PROGRESS = 'in_progress';
// export const ACTION_COMPLETED = 'completed';
// export const ACTION_OVERDUE = 'overdue';

// export const ACTION_STATUS_OPTIONS = [
//   { value: ACTION_PENDING, label: "Pending" },
//   { value: ACTION_IN_PROGRESS, label: "In Progress" },
//   { value: ACTION_COMPLETED, label: "Completed" },
//   { value: ACTION_OVERDUE, label: "Overdue" },
// ];

// // ============================================
// // RESOURCE TYPES
// // ============================================
// export const RESOURCE_DOCUMENT = 'document';
// export const RESOURCE_PRESENTATION = 'presentation';
// export const RESOURCE_VIDEO = 'video';
// export const RESOURCE_HANDBOOK = 'handbook';
// export const RESOURCE_SCORECARD = 'scorecard';

// export const RESOURCE_TYPE_OPTIONS = [
//   { value: RESOURCE_DOCUMENT, label: "Document" },
//   { value: RESOURCE_PRESENTATION, label: "Presentation" },
//   { value: RESOURCE_VIDEO, label: "Video" },
//   { value: RESOURCE_HANDBOOK, label: "Handbook" },
//   { value: RESOURCE_SCORECARD, label: "Scorecard" },
// ];

// // ============================================
// // ASSESSMENT/SSA STATUS
// // ============================================
// export const SSA_DRAFT = 'draft';
// export const SSA_SUBMITTED = 'submitted';
// export const SSA_UNDER_REVIEW = 'under_review';
// export const SSA_VALIDATED = 'validated';
// export const SSA_REJECTED = 'rejected';

// export const ASSESSMENT_STATUS_OPTIONS = [
//   { value: SSA_DRAFT, label: "Draft" },
//   { value: SSA_SUBMITTED, label: "Submitted" },
//   { value: SSA_UNDER_REVIEW, label: "Under Review" },
//   { value: SSA_VALIDATED, label: "Validated" },
//   { value: SSA_REJECTED, label: "Rejected" },
// ];

// // ============================================
// // OUTCOME AREAS
// // ============================================
// export const OUTCOME_SUSTAINABILITY = 'sustainability';
// export const OUTCOME_DISCIPLESHIP = 'discipleship';
// export const OUTCOME_LEARNING = 'learning_outcomes';

// export const OUTCOME_AREA_OPTIONS = [
//   { value: OUTCOME_SUSTAINABILITY, label: "Sustainability" },
//   { value: OUTCOME_DISCIPLESHIP, label: "Discipleship" },
//   { value: OUTCOME_LEARNING, label: "Learning Outcomes" },
// ];

// // ============================================
// // INDICATOR LEVELS
// // ============================================
// export const LEVEL_BEGINNING = 1;
// export const LEVEL_DEVELOPING = 2;
// export const LEVEL_PROFICIENT = 3;
// export const LEVEL_ACCOMPLISHED = 4;
// export const LEVEL_EXEMPLARY = 5;

// export const INDICATOR_LEVEL_OPTIONS = [
//   { value: LEVEL_BEGINNING, label: "Beginning" },
//   { value: LEVEL_DEVELOPING, label: "Developing" },
//   { value: LEVEL_PROFICIENT, label: "Proficient" },
//   { value: LEVEL_ACCOMPLISHED, label: "Accomplished" },
//   { value: LEVEL_EXEMPLARY, label: "Exemplary" },
// ];

// // ============================================
// // TRAINING TYPES
// // ============================================
// export const TRAINING_HANDBOOK = 'handbook_topic';
// export const TRAINING_CUSTOM = 'custom';
// export const TRAINING_EXTERNAL = 'external_partner';

// export const TRAINING_TYPE_OPTIONS = [
//   { value: TRAINING_HANDBOOK, label: "Handbook Topic" },
//   { value: TRAINING_CUSTOM, label: "Custom" },
//   { value: TRAINING_EXTERNAL, label: "External Partner" },
// ];

// // ============================================
// // TARGET AUDIENCE
// // ============================================
// export const AUDIENCE_ALL = 'all_staff';
// export const AUDIENCE_PROPRIETORS = 'proprietors';
// export const AUDIENCE_TEACHERS = 'teachers';
// export const AUDIENCE_ACCOUNTANTS = 'accountants';
// export const AUDIENCE_IT_TEACHERS = 'it_teachers';
// export const AUDIENCE_CT_TEACHERS = 'ct_teachers';

// export const TARGET_AUDIENCE_OPTIONS = [
//   { value: AUDIENCE_ALL, label: "All Staff" },
//   { value: AUDIENCE_PROPRIETORS, label: "Proprietors" },
//   { value: AUDIENCE_TEACHERS, label: "Teachers" },
//   { value: AUDIENCE_ACCOUNTANTS, label: "Accountants" },
//   { value: AUDIENCE_IT_TEACHERS, label: "IT Teachers" },
//   { value: AUDIENCE_CT_TEACHERS, label: "CT Teachers" },
// ];

// // ============================================
// // TRAINER TYPES
// // ============================================
// export const TRAINER_COORDINATOR = 'coordinator';
// export const TRAINER_CHAMPION = 'champion';
// export const TRAINER_SCHOOL_LEADER = 'school_leader';
// export const TRAINER_EDIFY_STAFF = 'edify_staff';
// export const TRAINER_EXTERNAL = 'external_partner';

// export const TRAINER_TYPE_OPTIONS = [
//   { value: TRAINER_COORDINATOR, label: "Coordinator" },
//   { value: TRAINER_CHAMPION, label: "Champion" },
//   { value: TRAINER_SCHOOL_LEADER, label: "School Leader" },
//   { value: TRAINER_EDIFY_STAFF, label: "Edify Staff" },
//   { value: TRAINER_EXTERNAL, label: "External Partner" },
// ];

// // ============================================
// // VENUE TYPES
// // ============================================
// export const VENUE_IN_PERSON = 'in_person';
// export const VENUE_VIRTUAL = 'virtual';
// export const VENUE_HYBRID = 'hybrid';

// export const VENUE_TYPE_OPTIONS = [
//   { value: VENUE_IN_PERSON, label: "In Person" },
//   { value: VENUE_VIRTUAL, label: "Virtual" },
//   { value: VENUE_HYBRID, label: "Hybrid" },
// ];

// // ============================================
// // ATTENDANCE STATUS
// // ============================================
// export const ATTENDANCE_REGISTERED = 'registered';
// export const ATTENDANCE_ATTENDED = 'attended';
// export const ATTENDANCE_ABSENT = 'absent';
// export const ATTENDANCE_CANCELLED = 'cancelled';

// export const ATTENDANCE_STATUS_OPTIONS = [
//   { value: ATTENDANCE_REGISTERED, label: "Registered" },
//   { value: ATTENDANCE_ATTENDED, label: "Attended" },
//   { value: ATTENDANCE_ABSENT, label: "Absent" },
//   { value: ATTENDANCE_CANCELLED, label: "Cancelled" },
// ];

// // ============================================
// // PAYMENT METHODS
// // ============================================
// export const PAYMENT_CASH = 'cash';
// export const PAYMENT_MOBILE_MONEY = 'mobile_money';
// export const PAYMENT_BANK_TRANSFER = 'bank_transfer';
// export const PAYMENT_CHECK = 'check';

// export const PAYMENT_METHOD_OPTIONS = [
//   { value: PAYMENT_CASH, label: "Cash" },
//   { value: PAYMENT_MOBILE_MONEY, label: "Mobile Money" },
//   { value: PAYMENT_BANK_TRANSFER, label: "Bank Transfer" },
//   { value: PAYMENT_CHECK, label: "Check" },
// ];

// // ============================================
// // PAYMENT STATUS
// // ============================================
// export const PAYMENT_PENDING = 'pending';
// export const PAYMENT_PARTIAL = 'partial';
// export const PAYMENT_PAID = 'paid';
// export const PAYMENT_OVERDUE = 'overdue';

// export const PAYMENT_STATUS_OPTIONS = [
//   { value: PAYMENT_PENDING, label: "Pending" },
//   { value: PAYMENT_PARTIAL, label: "Partial" },
//   { value: PAYMENT_PAID, label: "Paid" },
//   { value: PAYMENT_OVERDUE, label: "Overdue" },
// ];

// // ============================================
// // REPORT TYPES
// // ============================================
// export const REPORT_SCHOOL_PERFORMANCE = 'school_performance';
// export const REPORT_PARISH_SUMMARY = 'parish_summary';
// export const REPORT_ASSESSMENT_TRENDS = 'assessment_trends';
// export const REPORT_MEETING_ANALYTICS = 'meeting_analytics';
// export const REPORT_TRAINING_IMPACT = 'training_impact';
// export const REPORT_FINANCIAL_SUMMARY = 'financial_summary';
// export const REPORT_COORDINATOR_PERFORMANCE = 'coordinator_performance';

// export const REPORT_TYPE_OPTIONS = [
//   { value: REPORT_SCHOOL_PERFORMANCE, label: "School Performance" },
//   { value: REPORT_PARISH_SUMMARY, label: "Parish Summary" },
//   { value: REPORT_ASSESSMENT_TRENDS, label: "Assessment Trends" },
//   { value: REPORT_MEETING_ANALYTICS, label: "Meeting Analytics" },
//   { value: REPORT_TRAINING_IMPACT, label: "Training Impact" },
//   { value: REPORT_FINANCIAL_SUMMARY, label: "Financial Summary" },
//   { value: REPORT_COORDINATOR_PERFORMANCE, label: "Coordinator Performance" },
// ];

// // ============================================
// // QUARTERS
// // ============================================
// export const QUARTER_OPTIONS = [
//   { value: 1, label: "Q1" },
//   { value: 2, label: "Q2" },
//   { value: 3, label: "Q3" },
//   { value: 4, label: "Q4" },
// ];

// // ============================================
// // UGANDA REGIONS
// // ============================================
// export const UGANDA_REGIONS = [
//   { value: "central", label: "Central" },
//   { value: "eastern", label: "Eastern" },
//   { value: "northern", label: "Northern" },
//   { value: "western", label: "Western" },
// ];

// // ============================================
// // CURRENCY
// // ============================================
// export const DEFAULT_CURRENCY = 'UGX';

// // ============================================
// // FILE EXTENSIONS
// // ============================================
// export const ALLOWED_FILE_EXTENSIONS = [
//   'pdf', 'png', 'svg', 'jpg', 'jpeg', 
//   'docx', 'xlsx', 'doc', 'ppt', 'pptx'
// ];
